import * as React from 'react'
import * as WpsRuntime from 'DVWebNodeWpsRuntime'
import { ErrorAlert, InfoAlert, WarningAlert } from '@amzn/dvui/dist/Alert'
import { Icon, IconType } from '@amzn/dvui/dist/Icon'
import { Text } from '@amzn/dvui/dist/Text'
import * as st from './playbackMessaging.module.scss'
import { LogoMessage } from '../../selectors/component/actionBoxComponentSelectors'
import { ConditionalTextArray } from '../../utils/conditionalText'
import { TextLocalizer } from '../localizer/localizer'
import { StringId } from '../localizer/stringId'
import { ResponsiveImage } from '../images/responsiveImage'
import { BaseType, SrcType } from '../../utils/imageUtils'
import * as cn from 'classnames'
import * as stUtility from '../utilityClasses.module.scss'
import { AtfTreatmentProps } from '../../reducers/features'
import { isTreatment } from '../../utils/utils'

export interface IPlaybackMessagingProps extends AtfTreatmentProps {
  acceptedPreorder?: WpsRuntime.atv.wps.AcceptedPreorderActionProps
  notificationWarnings?: WpsRuntime.atv.wps.DVStringList
  subProblemMessages?: WpsRuntime.atv.wps.DVStringList
  infoAlertMessages?: WpsRuntime.atv.wps.DVStringList
  messagesWithLogo?: LogoMessage[]
  topLevelTvodMessages?: WpsRuntime.atv.wps.DVStringList
  maturityLockText?: string
  offerAvailableOn?: string
  formattedOfferAvailableOn?: string
  removeMessagesWithLogo?: boolean,
  classNames?: (string | { [cn: string]: boolean; })[]
}

export const LOGO_HEIGHT = 35

export const MaturityLockMessage = ({ message }: { message?: string }) => message
  ? <p className={st.maturityLockMessage}><Icon use={IconType.LOCK} className={st.icon} /><Text string={message} /></p>
  : null

//TODO(AVWebDP-452): Remove once ComingSoon fully migrated to WPS
const MILLIS_IN_180_DAYS = 180 * 24 * 60 * 60 * 1000
const MILLIS_IN_3_YEARS = 3 * 365 * 24 * 60 * 60 * 1000
const MILLIS_IN_5_YEARS = 5 * 365 * 24 * 60 * 60 * 1000

interface ComingSoonMessageProps {
  offerAvailableOn?: string
  formattedOfferAvailableOn?: string
}
const ComingSoonMessage = ({ offerAvailableOn, formattedOfferAvailableOn }: ComingSoonMessageProps) => {
  if (!offerAvailableOn || !formattedOfferAvailableOn) {
    return null
  }
  const offerAvailableDate = new Date(offerAvailableOn)
  const millisUntilAvailable = offerAvailableDate.getTime() - Date.now()

  const comingRealSoon =
    <TextLocalizer stringID={StringId.DV_DP_COMING_SOON_MESSAGE} replace={{ date: formattedOfferAvailableOn }} />
  const comingKindaSoon =
    <TextLocalizer stringID={StringId.DV_DP_COMING_IN_YEAR_MESSAGE}
                   replace={{ year: offerAvailableDate.getUTCFullYear().toString() }} />
  const comingInAWhile =
    <TextLocalizer stringID={StringId.DV_DP_COMING_SOON_TBC_DATE} />

  const localizedMessage =
    (millisUntilAvailable < 0) ? null :
      (millisUntilAvailable < MILLIS_IN_180_DAYS) ? comingRealSoon :
        (millisUntilAvailable < MILLIS_IN_3_YEARS) ? comingKindaSoon :
          (millisUntilAvailable < MILLIS_IN_5_YEARS) ? comingInAWhile :
            null

  return localizedMessage
    ? <InfoAlert>{localizedMessage}</InfoAlert> : null
}

const getMessagesWithLogo = ({ messagesWithLogo, atfTreatment, removeMessagesWithLogo }: IPlaybackMessagingProps) => {
  const filteredMessages = isTreatment(atfTreatment, 'T1', 'T2', 'T3') ? filterLogoMessages(messagesWithLogo, removeMessagesWithLogo) : messagesWithLogo
  return (
    !filteredMessages ? null :
      filteredMessages
        .filter(message => Boolean(message && message.text && message.text.string))
        .map((message, index) => {
          const text = <Text string={message.text}/>

          if (text && !message.logo) { // If we have a message but no logo use info alert to display the message
            return <InfoAlert key={`message-logo-alert-${index}`}
                              className={cn(st.logoMessage, { [st.logoInfoAlert]: isTreatment(atfTreatment, 'T1', 'T2', 'T3') })}>{text}</InfoAlert>
          }

          const logoImgProps = {
            alt: '',
            src: message.logo,
            baseValue: LOGO_HEIGHT,
            baseType: BaseType.HEIGHT,
            format: SrcType.png
          }

          const logo = <ResponsiveImage className={cn(st.logo, { [st.logoRedesign]: isTreatment(atfTreatment, 'T1', 'T2', 'T3') })} {...logoImgProps} />
          return (
            <p key={`message-logo-${index}`}
               className={cn(st.logoMessage, { [st.logoMessageRedesign]: isTreatment(atfTreatment, 'T1', 'T2', 'T3') })}>
              {logo}
              {isTreatment(atfTreatment, 'T1', 'T2', 'T3') && <span className={st.conditionalBreak} ><br/></span> }
              <strong>{text}</strong>
            </p>
          )
        })
  )
}

interface AcceptedPreorderProps {
  preorder?: WpsRuntime.atv.wps.AcceptedPreorderActionProps
}
const AcceptedPreorder = ({ preorder }: AcceptedPreorderProps) => {
  if (!preorder) {
    return null
  }

  const { body, cancelOrderText, title, displayMessages } = preorder

  return (
    <div className={st.acceptedPreorder}>
      <h2>{title}</h2>
      <Text string={body} tag="p" />
      {displayMessages.map(message => <Text string={message} tag="p" />)}
      <Text string={cancelOrderText} tag="p" />
    </div>
  )
}

// Depending on removeMessagesWithLogo we either remove the messages with a logo or remove the messages without a logo
const filterLogoMessages = (messagesWithLogo?: LogoMessage[], removeMessagesWithLogo: boolean = false) =>
  (messagesWithLogo || []).filter((message) => removeMessagesWithLogo === !message.logo)

export const PlaybackMessaging = ({
                                    acceptedPreorder,
                                    notificationWarnings,
                                    subProblemMessages,
                                    infoAlertMessages,
                                    messagesWithLogo,
                                    topLevelTvodMessages,
                                    maturityLockText,
                                    offerAvailableOn,
                                    formattedOfferAvailableOn,
                                    atfTreatment,
                                    removeMessagesWithLogo = false,
                                    classNames = [],
                                  }: IPlaybackMessagingProps) => (
  <div className={cn(st.wrapper, st.messages, stUtility.fullWidth, 'av-playback-messages', ...classNames)}>
    <ConditionalTextArray Tag={ErrorAlert} messages={subProblemMessages} />
    <MaturityLockMessage message={maturityLockText} />
    <AcceptedPreorder preorder={acceptedPreorder} />
    <ConditionalTextArray Tag={WarningAlert} messages={notificationWarnings} />
    <ConditionalTextArray Tag={InfoAlert} messages={infoAlertMessages} />
    <ConditionalTextArray Tag={InfoAlert} messages={topLevelTvodMessages} />
    <ComingSoonMessage
      offerAvailableOn={offerAvailableOn}
      formattedOfferAvailableOn={formattedOfferAvailableOn} />
    {getMessagesWithLogo({ messagesWithLogo, atfTreatment, removeMessagesWithLogo })}
  </div>
)